/**
 * L'anneau de score.
 *
 * La signature visuelle de cette direction : le score de prospection est la
 * seule donnée du produit qui vaut un dessin plutôt qu'un chiffre nu, parce
 * que c'est la seule qu'on compare d'un coup d'œil en parcourant une liste.
 *
 * L'anneau se lit avant le chiffre — un arc à moitié plein, c'est un prospect
 * moyen, sans avoir à lire « 7 ». Le chiffre reste là pour trancher entre deux
 * arcs voisins.
 */

import type { ProspectPriority } from '../../types';

/**
 * Le score ne va pas de 0 à 100 : il va de 0 à 10.
 *
 * C'est `MAX_PROSPECT_SCORE` dans `server/src/domain/scoring.ts` — rien de
 * vendable en ligne (6) + bonne note (2) + assez d'avis (2). Sans cette borne,
 * un anneau calculé sur 100 resterait toujours presque vide et ne
 * distinguerait plus rien.
 *
 * Recopié plutôt qu'importé : le client ne partage pas de code avec le
 * serveur. Si les poids changent là-bas, cette constante suit — mais l'anneau
 * ne ment jamais de plus d'un cran entre-temps, il se remplit seulement un peu
 * trop ou un peu trop peu.
 */
export const MAX_PROSPECT_SCORE = 10;

interface ScoreRingProps {
  score: number;
  size?: number;
  /** Épaisseur du trait. Suit la taille par défaut. */
  stroke?: number;
  /** Teinte de l'arc : une variable de la rampe d'opportunité. */
  tone?: string;
  /** Étiquette lue par les lecteurs d'écran, si le contexte ne suffit pas. */
  label?: string;
}

export function ScoreRing({
  score,
  size = 52,
  stroke = size >= 56 ? 6 : 5,
  tone = 'var(--brand-light)',
  label,
}: ScoreRingProps) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  // Borné : un score hors plage dessinerait un arc plus long que le cercle.
  const ratio = Math.min(1, Math.max(0, score / MAX_PROSPECT_SCORE));
  const filled = ratio * circumference;
  const center = size / 2;

  return (
    <span
      className="ring"
      style={{ width: size, height: size }}
      {...(label === undefined
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': label })}
    >
      <svg width={size} height={size} aria-hidden="true">
        <circle
          className="ring-track"
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="ring-value"
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={tone}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${filled.toFixed(1)} ${circumference.toFixed(1)}`}
        />
      </svg>
      <span className="ring-num" style={{ fontSize: size >= 56 ? 17 : 15.5 }}>
        {score}
      </span>
    </span>
  );
}

/**
 * La teinte de l'arc suit la priorité, pas le score brut.
 *
 * Trois paliers et non un dégradé continu : l'œil ne distingue pas 7 de 8 sur
 * une teinte interpolée, alors qu'il voit immédiatement un changement de
 * palier. Et surtout, la priorité est déjà calculée par le serveur — la
 * dériver ici une seconde fois créerait deux vérités qui finiraient par
 * diverger au premier ajustement des seuils.
 *
 * Bleu, vert, rouge : un code d'appel à passer, pas une rampe de température.
 * Le rouge marque ici ce qui ne vaut pas l'appel, pas une alerte.
 */
export function priorityRingTone(priority: ProspectPriority): string {
  if (priority === 'HIGH') return 'var(--prio-high)';
  if (priority === 'MEDIUM') return 'var(--prio-medium)';
  return 'var(--prio-low)';
}
